import React from "react";
import PropTypes from "prop-types";
import parse from "html-react-parser";
import { useMount } from "react-use";

import { track, ANALYTICS_ACTIONS } from "~/core/analytics";
import { smoothScroll } from "~/utils/scroll";
import { Grid } from "~/components/grid";
import { Card } from "~/components/card";
import { HeroSubheadline } from "~/components/typography";
import { Blank, ArrowIcon } from "~/components/icons";
import { Button } from "~/components/button";
import {
  Envelope,
  FlagUSA,
  Recycle,
  SquareFoot,
} from "~/components/icons";

import {
  PdpWrapper,
  PdpHeroWrapper,
  PdpImage,
  PdpInfo,
  PdpFinePrint,
  PdpInfoBlock,
  PdpInfoItem,
  PdpInfoIcon,
  PdpInfoLabel,
  PdpEyebrow,
} from "./pdp.style";

export const Pdp = ({
  product,
  setMessage,
  isLandscape,
  onNext,
}) => {
  const {
    images = [],
    variants = [],
    descriptionHtml,
  } = product;

  const width = "100%";
  const sizeLabel = isLandscape
    ? "A2 (5.5\" x 4.25\")"
    : "A2 (4.25\" x 5.5\")"
  const variant = variants
    .sort((a, b) =>
      parseFloat(b.price) - parseFloat(a.price)
    )[0];
  const sku = variant?.sku || 'TGS';
  const price = variant?.price;
  const ctaLabel = `$${price}  |  Buy Blank`;
  const onPersonalize = () => {
    const scrollToEl = document.querySelector("#message");
    const scrollPosition = scrollToEl.getBoundingClientRect().top + window.scrollY;
    smoothScroll(scrollPosition, 100, "inOutCubic");
    track.click({
      action: ANALYTICS_ACTIONS.PERSONALIZE,
      label: "Personalize Card",
    });
  }

  const onBuyBlank = () => {
    setMessage("");
    onNext();
    track.addToCart({
      id: sku,
      name: product.title,
      brand: product.vendor,
      quantity: 1,
      price,
      variant: 'Blank',
    })
  }

  useMount(() => {
    track.viewItem({
      id: sku,
      name: product.title,
      brand: product.vendor,
      quantity: 1,
      price,
    })
  })

  return (
    <PdpWrapper>
      <Grid>
        <PdpHeroWrapper>
          <PdpImage>
            {images.map(({ id, src, altText }) =>
              <Card
                key={id}
                {...{
                  alt: altText,
                  front: src,
                  isLandscape,
                  width,
                }}
              />
            )}
          </PdpImage>

          {product && (
            <>
              <PdpInfo>
                <PdpInfoBlock>
                  <PdpEyebrow to={`/designers/${product.vendorSlug}`}>{product.vendor}</PdpEyebrow>
                  <HeroSubheadline>{product.title}</HeroSubheadline>
                </PdpInfoBlock>
                <PdpInfoBlock>
                  <Button
                    icon={<ArrowIcon />}
                    label="Personalize Card"
                    onClick={onPersonalize}
                    fullWidth
                  />
                  <Button
                    label={ctaLabel}
                    variant="secondaryDark"
                    onClick={onBuyBlank}
                    fullWidth
                  />
                  <PdpFinePrint>Includes Free Shipping</PdpFinePrint>
                </PdpInfoBlock>
                {descriptionHtml && (
                  <PdpInfoBlock>
                    {parse(descriptionHtml)}
                  </PdpInfoBlock>
                )}
                <PdpInfoBlock>
                  <PdpInfoItem>
                    <PdpInfoIcon><SquareFoot /></PdpInfoIcon>
                    <PdpInfoLabel>{sizeLabel}</PdpInfoLabel>
                  </PdpInfoItem>
                  <PdpInfoItem>
                    <PdpInfoIcon><Blank /></PdpInfoIcon>
                    <PdpInfoLabel>Available blank or personalized printed message inside.</PdpInfoLabel>
                  </PdpInfoItem>
                  <PdpInfoItem>
                    <PdpInfoIcon><Envelope /></PdpInfoIcon>
                    <PdpInfoLabel>Includes matching envelope.</PdpInfoLabel>
                  </PdpInfoItem>
                  <PdpInfoItem>
                    <PdpInfoIcon><FlagUSA /></PdpInfoIcon>
                    <PdpInfoLabel>Printed in the United States.</PdpInfoLabel>
                  </PdpInfoItem>
                  <PdpInfoItem>
                    <PdpInfoIcon><Recycle /></PdpInfoIcon>
                    <PdpInfoLabel>100% recyclable post-consumer waste.</PdpInfoLabel>
                  </PdpInfoItem>
                  <PdpInfoItem>
                    <PdpInfoIcon><Recycle /></PdpInfoIcon>
                    <PdpInfoLabel>FSC® Certified, Green Seal Certified.</PdpInfoLabel>
                  </PdpInfoItem>
                </PdpInfoBlock>
              </PdpInfo>
            </>
          )}
        </PdpHeroWrapper>
      </Grid>
    </PdpWrapper>
  );
};
Pdp.propTypes = {
  product: PropTypes.object,
  setMessage: PropTypes.func,
  isLandscape: PropTypes.bool,
  onNext: PropTypes.func,
};
